import { Person, Remove, Groups2Outlined } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Button, CircularProgress, Collapse, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { FieldArray, Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { MemberStatuses } from '../../utils/mappings';
import { ICareTeam } from '../../Models/CareTeams/CareTeam.model';
import { ITeamRole } from '../../Models/CareTeams/TeamRole.model';
import { ITeamRoleAlternate } from '../../Models/CareTeams/TeamRoleAlternate.model';
import { TeamWorkingHours } from '../../Models/CareTeams/TeamWorkingHours.model';
import { Member } from '../../Models/Member/Member.model';
import IProviderSelectOption from '../../Models/Provider/ProviderSelectOption.model';
import IRole from '../../Models/Role/Role.model';
import { RootState } from '../../reducers';
import { openConfirmDeleteTeamModal, updateTeam } from '../../store/careTeams.slice';
import { apiString } from '../../utils/constants';
import ConfirmDeleteTeamModal from './modals/ConfirmDeleteTeamModal';
import SaveDiffModal from './SaveDiffModal';
import TeamAddressForm from './TeamAddressForm';
import SelectAlternateProviderDialog from './SelectAlternateProviderDialog';
import AlternateProviderBadge from './AlternateProviderBadge';
import FormikTeamWorkingHours from '../FormikFields/FormikTeamWorkingHours';
import Authorization from '../../utils/Authorization';
import { formatWorkingDay } from '../../utils/teamWorkingHoursHelper';

const AddressSchema = Yup.object().shape({
    Address1: Yup.string().required('Address Line 1 is required'),
    Address2: Yup.string(),
    City: Yup.string().required('City is required'),
    State: Yup.string().required('State is required'),
    ZipCode: Yup.string()
        .required('Zip Code is required')
        .matches(/^\d{5}(-\d{4})?$/, 'Zip Code must be in the format 12345 or 12345-6789'),
});

const TeamRoleSchema = Yup.object().shape({
    RoleId: Yup.string().required('Role is required'),
    AssignedProviderId: Yup.string().required('Assigned Provider is required'),
});

const isValidTimeFormat = (time: string): boolean => /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(time);

const convertTimeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

const isBothTimesEmptyOrBothFilled = (startsAt: string, endsAt: string): boolean => (!startsAt && !endsAt) || (!!startsAt && !!endsAt);

const isEndTimeAfterStartTime = (startsAt: string, endsAt: string): boolean => convertTimeToMinutes(endsAt) > convertTimeToMinutes(startsAt);

const WorkingDaySchema = Yup.object().shape({
    StartsAt: Yup.string().test('valid-start-time', 'Must be in HH:mm format (e.g., 09:00)', function (value) {
        const { EndsAt } = this.parent;

        if (!value && !EndsAt) return true;

        if (!isBothTimesEmptyOrBothFilled(value || '', EndsAt)) {
            return this.createError({ message: 'Both start and end times are required' });
        }

        if (!isValidTimeFormat(value || '')) {
            return false;
        }

        return true;
    }),
    EndsAt: Yup.string().test('valid-end-time', 'Must be in HH:mm format (e.g., 17:00)', function (value) {
        const { StartsAt } = this.parent;

        if (!value && !StartsAt) return true;

        if (!isBothTimesEmptyOrBothFilled(StartsAt, value || '')) {
            return this.createError({ message: 'Both start and end times are required' });
        }

        if (!isValidTimeFormat(value || '')) {
            return false;
        }

        if (!isEndTimeAfterStartTime(StartsAt, value)) {
            return this.createError({ message: 'End time must be after start time' });
        }

        return true;
    }),
});

const WorkingHoursSchema = Yup.object().shape({
    Monday: WorkingDaySchema,
    Tuesday: WorkingDaySchema,
    Wednesday: WorkingDaySchema,
    Thursday: WorkingDaySchema,
    Friday: WorkingDaySchema,
    Saturday: WorkingDaySchema,
    Sunday: WorkingDaySchema,
});

const CareTeamSchema = Yup.object().shape({
    Name: Yup.string().required('Team Name is required'),
    MeetingAddress: AddressSchema,
    MaxTeamSize: Yup.number().required('Max Team Size is required').min(1, 'Must be at least 1'),
    TeamRoles: Yup.array().of(TeamRoleSchema).min(1, '*At least one team role is required'),
    WorkingHours: WorkingHoursSchema,
});

export interface CareTeamDetailsProps {
    roles: IRole[];
    providerOptions: IProviderSelectOption[];
    readOnly?: boolean;
}

const CareTeamDetails: React.FC<CareTeamDetailsProps> = ({ roles, providerOptions, readOnly = false }) => {
    const dispatch = useAppDispatch();
    const { selectedTeam } = useSelector((state: RootState) => state.careTeams);
    const [assignedProviderNames, setAssignedProviderNames] = useState<string[]>([]);
    const [assignedMembers, setAssignedMembers] = useState<Member[]>([]);
    const [loadingMembers, setLoadingMembers] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [showDiffModal, setShowDiffModal] = useState(false);
    const [diffs, setDiffs] = useState([]);
    const [pendingValues, setPendingValues] = useState<ICareTeam | null>(null);
    const [lastSubmittedValues, setLastSubmittedValues] = useState<ICareTeam | null>(null);
    const [formErrors, setFormErrors] = useState({});
    const [showErrors, setShowErrors] = useState(false);
    const [openAlternateDialog, setOpenAlternateDialog] = useState(false);
    const [selectedMember, setSelectedMember] = useState<Member | null>(null);

    const initialValues: ICareTeam = useMemo(
        () => ({
            Name: selectedTeam?.Name || '',
            MeetingAddress: selectedTeam?.MeetingAddress || {
                Address1: '',
                Address2: '',
                City: '',
                State: '',
                ZipCode: '',
                FullAddress: '',
                Country: '',
                Lat: 0,
                Lon: 0,
            },
            AssignedMemberIds: selectedTeam?.AssignedMemberIds || [],
            TeamRoles: selectedTeam?.TeamRoles || [],
            MaxTeamSize: selectedTeam?.MaxTeamSize || 1,
            Id: selectedTeam?.Id || '',
            TeamRoleAlternates: selectedTeam?.TeamRoleAlternates || [],
            IsDeleted: false,
            WorkingHours: selectedTeam?.WorkingHours || new TeamWorkingHours(),
        }),
        [selectedTeam]
    );

    const allowedRoles = useMemo(() => roles.filter((r) => r.IsCareTeamRole), [roles]);

    const checkAssignedProviders = async (careTeam: ICareTeam) => {
        try {
            const response = await axios.post(`${apiString}/CareTeams/GetProvidersAssignedToOtherTeams`, careTeam);
            if (response.status === 200) {
                setAssignedProviderNames(response.data);
            }
        } catch (error) {
            console.error('Error fetching assigned providers:', error);
        }
    };

    useEffect(() => {
        setAssignedProviderNames([]);
    }, [selectedTeam]);

    useEffect(() => {
        const fetchAssignedMembers = async () => {
            setLoadingMembers(true);
            try {
                const response = await axios.post(`${apiString}/member/getmembersbyids`, initialValues.AssignedMemberIds);
                if (response.status === 200) {
                    setAssignedMembers(response.data);
                }
            } catch (error) {
                console.error('Error fetching assigned members:', error);
            } finally {
                setLoadingMembers(false);
            }
        };

        if (initialValues.AssignedMemberIds.length > 0) {
            fetchAssignedMembers();
        }
    }, [initialValues.AssignedMemberIds]);

    const [openSections, setOpenSections] = useState({
        teamDetails: true,
        teamAddress: true,
        teamRoles: true,
        assignedMembers: true,
        teamWorkingHours: true,
    });

    const toggleSection = (section: keyof typeof openSections) => {
        setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const getTeamRolesDiff = (oldTeamRoles: ITeamRole[], newTeamRoles: ITeamRole[], allRoles: IRole[], allProviderOptions: IProviderSelectOption[]) => {
        const teamRoleDiffs: { label: string; oldValue: string; newValue: string }[] = [];
        const oldRolesMap = oldTeamRoles.reduce<Record<string, string>>((accumulator, teamRole) => {
            accumulator[teamRole.RoleId] = teamRole.AssignedProviderId;
            return accumulator;
        }, {});
        const newRolesMap = newTeamRoles.reduce<Record<string, string>>((accumulator, teamRole) => {
            accumulator[teamRole.RoleId] = teamRole.AssignedProviderId;
            return accumulator;
        }, {});
        Object.keys(oldRolesMap).forEach((roleId) => {
            if (newRolesMap[roleId] && oldRolesMap[roleId] !== newRolesMap[roleId]) {
                const roleName = allRoles.find((role) => role.Id === roleId)?.Name || roleId;
                const oldProviderName = allProviderOptions.find((provider) => provider.ProviderId === oldRolesMap[roleId])?.ProviderName || oldRolesMap[roleId];
                const newProviderName = allProviderOptions.find((provider) => provider.ProviderId === newRolesMap[roleId])?.ProviderName || newRolesMap[roleId];
                teamRoleDiffs.push({
                    label: `Swapped: ${roleName}`,
                    oldValue: oldProviderName,
                    newValue: newProviderName,
                });
            }
        });
        Object.keys(newRolesMap).forEach((roleId) => {
            if (!oldRolesMap[roleId]) {
                const roleName = allRoles.find((role) => role.Id === roleId)?.Name || roleId;
                const providerName = allProviderOptions.find((provider) => provider.ProviderId === newRolesMap[roleId])?.ProviderName || newRolesMap[roleId];
                teamRoleDiffs.push({
                    label: `Added: ${roleName}`,
                    oldValue: '',
                    newValue: providerName,
                });
            }
        });
        Object.keys(oldRolesMap).forEach((roleId) => {
            if (!newRolesMap[roleId]) {
                const roleName = allRoles.find((role) => role.Id === roleId)?.Name || roleId;
                const providerName = allProviderOptions.find((provider) => provider.ProviderId === oldRolesMap[roleId])?.ProviderName || oldRolesMap[roleId];
                teamRoleDiffs.push({
                    label: `Removed: ${roleName}`,
                    oldValue: providerName,
                    newValue: '',
                });
            }
        });
        return teamRoleDiffs.length ? teamRoleDiffs : null;
    };

    const calculateDiffs = useCallback(
        (previousCareTeamValues: ICareTeam, updatedCareTeamValues: ICareTeam) => {
            const careTeamDiffs: { label: string; oldValue: any; newValue: any }[] = [];
            if (previousCareTeamValues.Name !== updatedCareTeamValues.Name) {
                careTeamDiffs.push({
                    label: 'Team Name',
                    oldValue: previousCareTeamValues.Name ?? '',
                    newValue: updatedCareTeamValues.Name ?? '',
                });
            }
            if (previousCareTeamValues.MaxTeamSize !== updatedCareTeamValues.MaxTeamSize) {
                careTeamDiffs.push({
                    label: 'Max Team Size',
                    oldValue: previousCareTeamValues.MaxTeamSize ?? '',
                    newValue: updatedCareTeamValues.MaxTeamSize ?? '',
                });
            }
            const addressFields = ['Address1', 'Address2', 'City', 'State', 'ZipCode'];
            addressFields.forEach((addressField) => {
                const previousAddressValue = previousCareTeamValues.MeetingAddress?.[addressField] ?? '';
                const updatedAddressValue = updatedCareTeamValues.MeetingAddress?.[addressField] ?? '';
                if (previousAddressValue !== updatedAddressValue) {
                    careTeamDiffs.push({
                        label: `Address: ${addressField}`,
                        oldValue: previousAddressValue,
                        newValue: updatedAddressValue,
                    });
                }
            });
            if (JSON.stringify(previousCareTeamValues.TeamRoles) !== JSON.stringify(updatedCareTeamValues.TeamRoles)) {
                const teamRolesDiff = getTeamRolesDiff(previousCareTeamValues.TeamRoles, updatedCareTeamValues.TeamRoles, roles, providerOptions);
                if (teamRolesDiff) {
                    teamRolesDiff.forEach((diff) => {
                        careTeamDiffs.push({ label: diff.label, oldValue: diff.oldValue, newValue: diff.newValue });
                    });
                }
            }
            if (JSON.stringify(previousCareTeamValues.AssignedMemberIds) !== JSON.stringify(updatedCareTeamValues.AssignedMemberIds)) {
                careTeamDiffs.push({
                    label: 'Assigned Members',
                    oldValue: JSON.stringify(previousCareTeamValues.AssignedMemberIds),
                    newValue: JSON.stringify(updatedCareTeamValues.AssignedMemberIds),
                });
            }
            if (JSON.stringify(previousCareTeamValues.TeamRoleAlternates) !== JSON.stringify(updatedCareTeamValues.TeamRoleAlternates)) {
                const previousAlternates = previousCareTeamValues.TeamRoleAlternates || [];
                const updatedAlternates = updatedCareTeamValues.TeamRoleAlternates || [];

                const allMemberRoles = new Map<string, { memberId: string; roleId: string }>();
                previousAlternates.forEach((alt) => {
                    allMemberRoles.set(`${alt.MemberId}-${alt.RoleId}`, { memberId: alt.MemberId, roleId: alt.RoleId });
                });
                updatedAlternates.forEach((alt) => {
                    allMemberRoles.set(`${alt.MemberId}-${alt.RoleId}`, { memberId: alt.MemberId, roleId: alt.RoleId });
                });

                allMemberRoles.forEach(({ memberId, roleId }) => {
                    const prevAlt = previousAlternates.find((alt) => alt.MemberId === memberId && alt.RoleId === roleId);
                    const updAlt = updatedAlternates.find((alt) => alt.MemberId === memberId && alt.RoleId === roleId);

                    if (
                        prevAlt?.AssignedProviderId === updAlt?.AssignedProviderId &&
                        prevAlt?.MemberFirstName === updAlt?.MemberFirstName &&
                        prevAlt?.MemberLastName === updAlt?.MemberLastName
                    ) {
                        return;
                    }

                    const role = roles?.find((r) => r.Id === roleId);
                    const roleName = role?.Name || 'Unknown Role';

                    const oldValueText = prevAlt
                        ? `${prevAlt.MemberFirstName} ${prevAlt.MemberLastName} (${prevAlt.ProviderName})`
                        : `No Alternate for ${roleName}`;

                    const newValueText = updAlt
                        ? `${updAlt.MemberFirstName} ${updAlt.MemberLastName} (${updAlt.ProviderName})`
                        : `No Alternate for ${roleName}`;

                    careTeamDiffs.push({
                        label: `Alternate - ${roleName}`,
                        oldValue: oldValueText,
                        newValue: newValueText,
                    });
                });
            }

            const hasWorkingHoursChanged = JSON.stringify(previousCareTeamValues.WorkingHours) !== JSON.stringify(updatedCareTeamValues.WorkingHours);
            if (hasWorkingHoursChanged) {
                const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                days.forEach((day) => {
                    const previousDay = previousCareTeamValues.WorkingHours?.[day];
                    const updatedDay = updatedCareTeamValues.WorkingHours?.[day];

                    if (JSON.stringify(previousDay) !== JSON.stringify(updatedDay)) {
                        careTeamDiffs.push({
                            label: `Working Hours - ${day}`,
                            oldValue: previousDay ? formatWorkingDay(previousDay) : 'Not schedulable',
                            newValue: updatedDay ? formatWorkingDay(updatedDay) : 'Not schedulable',
                        });
                    }
                });
            }
            return careTeamDiffs;
        },
        [providerOptions, roles]
    );

    const handleConfirmSave = () => {
        if (pendingValues) {
            dispatch(updateTeam(pendingValues));
        }
        setShowDiffModal(false);
        setPendingValues(null);
    };

    const handleCancelSave = () => {
        setShowDiffModal(false);
        setPendingValues(null);
    };

    const handleOpenAlternateDialog = (member: Member) => {
        setSelectedMember(member);
        setOpenAlternateDialog(true);
    };

    const handleCloseAlternateDialog = () => {
        setOpenAlternateDialog(false);
        setSelectedMember(null);
    };

    const handleSaveAlternateProviders = (updatedAlternates: ITeamRoleAlternate[]) => {
        if (!selectedTeam) return;

        const updatedTeam: ICareTeam = {
            ...selectedTeam,
            TeamRoleAlternates: updatedAlternates,
        };

        const alternatesDiff = calculateDiffs(selectedTeam, updatedTeam);
        setDiffs(alternatesDiff);
        setPendingValues(updatedTeam);
        setShowDiffModal(true);
        handleCloseAlternateDialog();
    };

    const flattenErrors = (errors) => {
        const result = [];
        const recurse = (obj, prefix = '') => {
            Object.entries(obj).forEach(([key, value]) => {
                if (typeof value === 'string') {
                    result.push({ field: prefix ? `${prefix}.${key}` : key, error: value });
                } else if (typeof value === 'object' && value !== null) {
                    recurse(value, prefix ? `${prefix}.${key}` : key);
                }
            });
        };
        recurse(errors);
        return result;
    };

    useEffect(() => {
        if (Object.keys(formErrors).length === 0 && lastSubmittedValues) {
            const diffs = calculateDiffs(initialValues, lastSubmittedValues);
            setDiffs(diffs);
            setPendingValues(lastSubmittedValues);
            setShowDiffModal(true);
            setLastSubmittedValues(null);
        }
    }, [calculateDiffs, formErrors, initialValues, lastSubmittedValues]);

    return (
        <Box sx={{ position: 'relative' }}>
            {loadingMembers && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 20,
                        backdropFilter: 'blur(2px)',
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress />
                        <Typography>Loading team data...</Typography>
                    </Box>
                </Box>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={CareTeamSchema}
                onSubmit={async (values, formikHelpers) => {
                    const errors = await formikHelpers.validateForm(values);
                    setFormErrors(errors);
                    if (Object.keys(errors).length === 0) {
                        const diffs = calculateDiffs(initialValues, values);
                        setDiffs(diffs);
                        setPendingValues(values);
                        setShowDiffModal(true);
                        setShowErrors(false);
                    } else {
                        setShowErrors(true);
                    }
                }}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={false}
            >
                {({ values, errors, handleChange, handleBlur, touched, setFieldValue }) => (
                    <Form id="care-team-form">
                        {!readOnly && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button type="submit" variant="contained" color="primary" disabled={false}>
                                        Save
                                    </Button>
                                    <Button variant="contained" color="error" onClick={() => dispatch(openConfirmDeleteTeamModal())} disabled={false}>
                                        Delete
                                    </Button>
                                </Box>
                                {showErrors && Object.keys(formErrors).length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        {flattenErrors(formErrors).map(({ field, error }) => (
                                            <Typography key={field} color="error" variant="body2">
                                                {error}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Paper variant="outlined" sx={{ padding: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        {openSections.teamDetails ? (
                                            <Typography variant="subtitle1">Team Details</Typography>
                                        ) : (
                                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                {values.Name || 'No Name'} &nbsp;|&nbsp; Max Size: {values.MaxTeamSize}
                                            </Typography>
                                        )}
                                        <Button onClick={() => toggleSection('teamDetails')}>{openSections.teamDetails ? 'Collapse' : 'Expand'}</Button>
                                    </Box>
                                    <Collapse in={openSections.teamDetails}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, md: 8, lg: 10 }}>
                                                <TextField
                                                    sx={{ marginTop: 2 }}
                                                    fullWidth
                                                    label="Team Name"
                                                    name="Name"
                                                    value={values.Name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.Name && Boolean(errors.Name)}
                                                    helperText={touched.Name && errors.Name}
                                                    disabled={readOnly}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 4, lg: 2 }}>
                                                <TextField
                                                    sx={{ marginTop: 2 }}
                                                    fullWidth
                                                    label="Max Team Size"
                                                    name="MaxTeamSize"
                                                    type="number"
                                                    value={values.MaxTeamSize}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.MaxTeamSize && Boolean(errors.MaxTeamSize)}
                                                    helperText={touched.MaxTeamSize && errors.MaxTeamSize}
                                                    disabled={readOnly}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Collapse>
                                </Paper>
                            </Grid>
                            <Grid size={12}>
                                <Paper variant="outlined" sx={{ padding: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        {openSections.teamAddress ? (
                                            <Typography variant="subtitle1">Team Address</Typography>
                                        ) : (
                                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                {[
                                                    values.MeetingAddress.Address1,
                                                    values.MeetingAddress.Address2,
                                                    values.MeetingAddress.City,
                                                    values.MeetingAddress.State,
                                                    values.MeetingAddress.ZipCode,
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ') || 'No Address'}
                                            </Typography>
                                        )}
                                        <Button onClick={() => toggleSection('teamAddress')}>{openSections.teamAddress ? 'Collapse' : 'Expand'}</Button>
                                    </Box>
                                    <Collapse in={openSections.teamAddress}>
                                        <TeamAddressForm />
                                    </Collapse>
                                </Paper>
                            </Grid>
                            <Grid size={12}>
                                <Paper variant="outlined" sx={{ padding: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">Team Roster</Typography>
                                        <Button onClick={() => toggleSection('teamRoles')}>{openSections.teamRoles ? 'Collapse' : 'Expand'}</Button>
                                    </Box>
                                    <Collapse in={openSections.teamRoles}>
                                        {assignedProviderNames.length > 0 && (
                                            <Alert variant="outlined" sx={{ p: 1, my: 2 }} severity="info">
                                                The following providers would have multiple team assignments: {assignedProviderNames.join(', ')}
                                            </Alert>
                                        )}
                                        <FieldArray name="TeamRoles">
                                            {({ push, remove }) => (
                                                <>
                                                    {values.TeamRoles.map((role, index) => {
                                                        const availableProviders = providerOptions.filter((provider) => provider.RoleId === role.RoleId);

                                                        return (
                                                            <Box key={index} sx={{ marginBottom: 2 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                                    {selectedTeam?.TeamRoleAlternates && selectedTeam.TeamRoleAlternates.length > 0 && (
                                                                        <Box sx={{ pt: '4px', flexShrink: 0 }}>
                                                                            <AlternateProviderBadge
                                                                                alternates={selectedTeam.TeamRoleAlternates}
                                                                                roleId={role.RoleId}
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                    <Grid container spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
                                                                        <Grid size={5}>
                                                                            <Autocomplete
                                                                                options={allowedRoles}
                                                                                getOptionLabel={(option) => option.Name}
                                                                                onChange={(event, value) => {
                                                                                    setFieldValue(`TeamRoles.${index}.RoleId`, value?.Id || '');
                                                                                }}
                                                                                value={roles.find((r) => r.Id === role.RoleId) || null}
                                                                                renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        label="Role"
                                                                                        name={`TeamRoles.${index}.RoleId`}
                                                                                        onChange={handleChange}
                                                                                        onBlur={handleBlur}
                                                                                        error={
                                                                                            touched.TeamRoles?.[index]?.RoleId &&
                                                                                            Boolean((errors.TeamRoles?.[index] as ITeamRole)?.RoleId)
                                                                                        }
                                                                                        helperText={
                                                                                            touched.TeamRoles?.[index]?.RoleId &&
                                                                                            (errors.TeamRoles?.[index] as ITeamRole)?.RoleId
                                                                                        }
                                                                                        disabled={readOnly}
                                                                                    />
                                                                                )}
                                                                                disabled={readOnly}
                                                                            />
                                                                        </Grid>
                                                                        <Grid size={5}>
                                                                            <Autocomplete
                                                                                options={availableProviders}
                                                                                getOptionLabel={(option) => option.ProviderName}
                                                                                onChange={(event, value) => {
                                                                                    setFieldValue(
                                                                                        `TeamRoles.${index}.AssignedProviderId`,
                                                                                        value?.ProviderId || ''
                                                                                    );
                                                                                    setFieldValue(`TeamRoles.${index}.ProviderName`, value?.ProviderName || '');
                                                                                    const newValues = structuredClone(values);
                                                                                    newValues.TeamRoles[index].AssignedProviderId = value?.ProviderId || '';
                                                                                    checkAssignedProviders(newValues);
                                                                                }}
                                                                                value={
                                                                                    availableProviders.find((p) => p.ProviderId === role.AssignedProviderId) ||
                                                                                    null
                                                                                }
                                                                                disabled={!role.RoleId || readOnly}
                                                                                renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        label="Assigned Provider"
                                                                                        name={`TeamRoles.${index}.AssignedProviderId`}
                                                                                        onChange={handleChange}
                                                                                        onBlur={handleBlur}
                                                                                        error={
                                                                                            touched.TeamRoles?.[index]?.AssignedProviderId &&
                                                                                            Boolean(
                                                                                                (errors.TeamRoles?.[index] as ITeamRole)?.AssignedProviderId
                                                                                            )
                                                                                        }
                                                                                        helperText={
                                                                                            touched.TeamRoles?.[index]?.AssignedProviderId &&
                                                                                            (errors.TeamRoles?.[index] as ITeamRole)?.AssignedProviderId
                                                                                        }
                                                                                        disabled={!role.RoleId || readOnly}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </Grid>
                                                                        {!readOnly && (
                                                                            <Grid size={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <IconButton onClick={() => remove(index)}>
                                                                                    <Remove />
                                                                                </IconButton>
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                    {errors.TeamRoles && typeof errors.TeamRoles === 'string' && (
                                                        <Grid size={12}>
                                                            <Typography color="error">{errors.TeamRoles}</Typography>
                                                        </Grid>
                                                    )}

                                                    {!readOnly && (
                                                        <Grid size={12}>
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                onClick={() => push({ RoleId: '', AssignedProviderId: '', AlternateProviderPerMemberMap: {} })}
                                                            >
                                                                Add Role
                                                            </Button>
                                                        </Grid>
                                                    )}
                                                </>
                                            )}
                                        </FieldArray>
                                    </Collapse>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Paper variant="outlined" sx={{ padding: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">Team Working Hours</Typography>
                                        <Button onClick={() => toggleSection('teamWorkingHours')}>
                                            {openSections.teamWorkingHours ? 'Collapse' : 'Expand'}
                                        </Button>
                                    </Box>
                                    <Collapse in={openSections.teamWorkingHours}>
                                        <Box sx={{ mt: 2 }}>
                                            <FormikTeamWorkingHours
                                                namePrefix="WorkingHours"
                                                readOnly={readOnly || !new Authorization().isInPermission('Manage teams')}
                                            />
                                        </Box>
                                    </Collapse>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Paper variant="outlined" sx={{ padding: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">Assigned Members</Typography>
                                        <Button onClick={() => toggleSection('assignedMembers')}>{openSections.assignedMembers ? 'Collapse' : 'Expand'}</Button>
                                    </Box>
                                    <Collapse in={openSections.assignedMembers}>
                                        {loadingMembers ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : (
                                            <Box sx={{ height: 400, width: '100%' }}>
                                                <Paper sx={{ height: 400, width: '100%', overflow: 'auto' }}>
                                                    <DataGrid
                                                        getRowId={(row) => row.id}
                                                        rows={assignedMembers.map((member) => ({
                                                            id: member.Id,
                                                            name: `${member.FirstName} ${member.LastName}`,
                                                            ahcccsId: member.Mem_AHCCCS_ID,
                                                            dob: new Date(member.DOB).toLocaleDateString(),
                                                            status: MemberStatuses.find((s) => s.Id === member.Status)?.Name || member.Status,
                                                        }))}
                                                        columns={[
                                                            { field: 'name', headerName: 'Name', flex: 1, sortable: true, filterable: true },
                                                            { field: 'ahcccsId', headerName: 'AHCCCS ID', flex: 1, sortable: true, filterable: true },
                                                            { field: 'dob', headerName: 'DOB', flex: 1, sortable: true, filterable: true },
                                                            { field: 'status', headerName: 'Status', flex: 1, sortable: true, filterable: true },
                                                            {
                                                                field: 'actions',
                                                                headerName: 'Actions',
                                                                flex: 1,
                                                                sortable: false,
                                                                filterable: false,
                                                                renderCell: (params) => (
                                                                    <>
                                                                        <Tooltip title="View Member Chart">
                                                                            <IconButton
                                                                                component={Link}
                                                                                to={`/provider/members-list/member-chart/${params.row.id}/member-profile`}
                                                                                color="primary"
                                                                            >
                                                                                <Person />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        {!readOnly && (
                                                                            <Tooltip title="Select Alternate Provider">
                                                                                <IconButton
                                                                                    color="secondary"
                                                                                    onClick={() => {
                                                                                        const member = assignedMembers.find((m) => m.Id === params.row.id);
                                                                                        if (member) {
                                                                                            handleOpenAlternateDialog(member);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <Groups2Outlined />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </>
                                                                ),
                                                            },
                                                        ]}
                                                        pageSizeOptions={[5, 10, 25, 50]}
                                                        paginationModel={{ pageSize: pageSize, page: page }}
                                                        onPaginationModelChange={(model) => {
                                                            setPageSize(model.pageSize);
                                                            setPage(model.page);
                                                        }}
                                                        disableRowSelectionOnClick
                                                    />
                                                </Paper>
                                            </Box>
                                        )}
                                    </Collapse>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
            <SaveDiffModal open={showDiffModal} diffs={diffs} onConfirm={handleConfirmSave} onCancel={handleCancelSave} />
            <ConfirmDeleteTeamModal />
            {selectedTeam && selectedMember && (
                <SelectAlternateProviderDialog
                    open={openAlternateDialog}
                    onClose={handleCloseAlternateDialog}
                    member={selectedMember}
                    roles={roles}
                    providerOptions={providerOptions}
                    selectedTeam={selectedTeam}
                    onSave={handleSaveAlternateProviders}
                />
            )}
        </Box>
    );
};

export default CareTeamDetails;
